import {
  Button,
  ButtonProps,
  createPolymorphicComponent,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { forwardRef } from "react";

// Define the full props for our component.
// It includes our custom props AND all original ButtonProps (except 'color').
interface _StyledActionButtonProps extends Omit<ButtonProps, "color"> {
  width?: string;
  buttonColor?: string;
  textColor?: string;
  textSize?: string;
  fontWeight?: number;
}

const _StyledActionButton = forwardRef<HTMLButtonElement, _StyledActionButtonProps>(
  (
    {
      width = "70%",
      buttonColor,
      textColor = "black",
      textSize = "1.3rem",
      fontWeight = 500,
      children,
      style,
      ...props
    },
    ref
  ) => {
    const theme = useMantineTheme();
    const finalButtonColor = buttonColor || theme.colors.yellow[0];

    return (
      <Button
        size="lg"
        {...props} // Pass all other ButtonProps
        ref={ref}
        color={finalButtonColor}
        style={{ color: textColor, width, borderRadius: "30px", ...(style as object) }}
      >
        <Text size={textSize} fw={fontWeight}>
          {children}
        </Text>
      </Button>
    );
  }
);

_StyledActionButton.displayName = "StyledActionButton";

// Create the final polymorphic component.
// The second generic argument must be the full props interface (_StyledActionButtonProps)
// so that it inherits all the Button props like 'size', 'variant', etc.
export const StyledActionButton = createPolymorphicComponent<"button", _StyledActionButtonProps>(
  _StyledActionButton
);
